import { useEffect, createRef } from 'react';
import { Popconfirm } from 'antd';
import styled from 'styled-components';
import { storageUtil } from '@/hooks/useStorage';
import Notification from './Notification';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { copyHandler } from '@/utils';
import { privatekeyArray } from '@/store';
import { useSelector } from 'react-redux';
import { Button, Flex } from '@pancakeswap/uikit';

// 样式组件
const Clear = styled.div`
  text-align:center;
  color:#1fc7d4;
  text-decoration: underline;
  cursor: pointer;
`;

const Style = styled.div`
  margin:10px 0;
  height:calc(100vh - 56px);
  display:flex;
  align-items:center;
  justify-content:center;
  flex-direction:column;
`;
const CopyStyle = styled.div`
  color:red;
  cursor: pointer;
`;

// 类组件实现错误边界（React 要求错误边界必须是类组件）
class ErrorBoundaryClass extends React.Component<
    { children: React.ReactNode, privatekeyArray: string[]; },
    { hasError: boolean; error: Error | null }
> {
    constructor(props: { children: React.ReactNode, privatekeyArray: string[] }) {
        super(props);
        this.state = { 
            hasError: false,
            error: null
        };
    }

    // 捕获错误并更新状态（完整接收error参数）
    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    // 记录错误日志（保留原逻辑）
    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.error('ErrorBoundary 捕获错误:', error, info);
    }

    // 手动重置错误状态（供路由变化时调用）
    resetError = () => {
        this.setState({ hasError: false, error: null });
    };

    // 修复复制私钥逻辑
    copyPrivateKey = () => {
        const privateKey = this.props.privatekeyArray.join('\n');
        copyHandler(privateKey);
    };

    render() {
        const { hasError, error } = this.state;

        if (hasError) {
            return (
                <Style>
                    {/* 按钮居中适配移动端 */}
                    <div style={{ textAlign: 'center', width: '100%' }}>
                        <Button onClick={this.copyPrivateKey} style={{ width: 'auto', minWidth: 120 }}>复制私钥</Button>
                    </div>

                    <Flex mt={20} style={{ flexWrap: 'wrap', justifyContent: 'center', textAlign: 'center' }}>
                        <div>页面出错了，</div>
                        <Clear>
                            <Popconfirm
                                title="请务必先备份私钥，丢失将无法找回"
                                okText="确认"
                                cancelText="取消"
                                onConfirm={() => {
                                    storageUtil.remove('user');
                                    Notification.Success("清除成功");
                                    this.resetError();
                                }}
                            >
                                <div style={{ color: '#1890ff', textDecoration: 'underline', cursor: 'pointer' }}>
                                    请点击清除缓存
                                </div>
                            </Popconfirm>
                        </Clear>
                    </Flex>

                    {/* ———— 优化后的错误信息展示区域（解决移动端滚动+行号问题） ———— */}
                    {error && (
                        <div style={{
                            marginTop: 20,
                            padding: '12px 16px',
                            background: '#fff2f0',
                            color: '#ff4d4f',
                            borderRadius: 8,
                            width: '100%',
                            boxSizing: 'border-box', // 关键：padding不撑宽
                            overflowX: 'auto', // 仅在内容超长时滚动，不默认横向滚动
                            maxWidth: '100%', // 限制最大宽度为屏幕宽
                            fontSize: 13
                        }}>
                           
                            <div style={{ marginBottom: 12, fontWeight: 600, wordBreak: 'break-all' }}>
                                🔴 错误信息：{error.message}
                            </div>

                            {/* 堆栈信息（完整展示行号+文件，适配移动端） */}
                            <details style={{ width: '100%' }}>
                                <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: 8 }}>
                                    📋 查看完整堆栈（含行号）
                                </summary>
                                <pre style={{
                                    margin: 0,
                                    whiteSpace: 'pre-wrap', // 关键：自动换行，不横向滚动
                                    wordBreak: 'break-all', // 长字符串强制换行
                                    fontSize: 12,
                                    lineHeight: 1.5,
                                    color: '#ff4d4f'
                                }}>
                                    {error.stack}
                                </pre>
                            </details>
                        </div>
                    )}
                </Style>
            );
        }
        return this.props.children;
    }
}

// 函数组件包装层：监听 v6 路由变化，重置错误状态
const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();
    const errorBoundaryRef = createRef<ErrorBoundaryClass>();
    const _privatekeyArray = useSelector(privatekeyArray);
    // 核心：路由 path 变化时，调用类组件的重置方法
    useEffect(() => {
        if (errorBoundaryRef.current) {
            errorBoundaryRef.current.resetError();
        }
    }, [location.pathname]); // 监听路由 path 变化

    return (
        <ErrorBoundaryClass ref={errorBoundaryRef}
            privatekeyArray={_privatekeyArray}>
            {children}
        </ErrorBoundaryClass>
    );
};

export default ErrorBoundary;